import type { AccommodationPoiData, DateRange } from '$lib/components/pois/types';
import type { Locale } from '$lib/i18n/types';
import type { BookingcomLabelBuildData, BookingcomLabelBuildDataNullable, BookingcomUrlBuildParams, field } from '$lib/services/bookingcom/types';
import type { Pagename } from '$lib/services/urls/routesPagename';
import type { SearchParams } from '$lib/services/urls/searchParams';
import type z from 'zod';
import { browser } from '$app/environment';
import { page } from '$app/state';
import { serializeLabelForBookingComUrl } from '$lib/services/bookingcom/bookingcomLabel';
import { BOOKING_LABEL_SCHEMA } from '$lib/services/bookingcom/types';
import { transformStringDateToCalendarDate } from '$lib/utils/date';
import { formatPrice } from '$lib/utils/formatPrice';
import { getBookingPlatform } from '$lib/utils/getPlatform';
import { languageTag } from '$paraglide/runtime';
import { type BookingcomPlatform, getBookingcomHomeUrl, getBookingcomSearchResultsUrl } from '@viamichelin/business/bookingcom';

/**
 * Search parameter utilities for Booking.com URLs
 */
const bookingComUrlSearchParam = {
	setLabel(bookingcomUrl: string, locale: Locale, pagename: Pagename, labelData: BookingcomLabelBuildDataNullable) {
		const label = serializeLabelForBookingComUrl(locale, pagename, labelData);
		const url = new URL(bookingcomUrl);
		url.searchParams.set('label', label);
		return url.toString();
	},

	setCheckinCheckout(baseBookingUrl: string, searchParams: SearchParams): string {
		const dateRange = searchParams?.filters?.ACCOMMODATION_AVAILABILITY_DATE_RANGE as DateRange;
		if (!dateRange) return baseBookingUrl;

		const url = new URL(baseBookingUrl);
		const checkin = transformStringDateToCalendarDate(dateRange.startDate);
		const checkout = transformStringDateToCalendarDate(dateRange.endDate);

		if (checkin) url.searchParams.set('checkin', checkin.toString());
		if (checkout) url.searchParams.set('checkout', checkout.toString());

		return url.toString();
	},

	hasCheckinCheckout(bookingcomUrl: string): boolean {
		const url = new URL(bookingcomUrl);
		return url.searchParams.has('checkin') && url.searchParams.has('checkout');
	},
};

const labelParams = {
	fromPoi(poi: AccommodationPoiData) {
		return {
			poiId: poi.id,
			poiSrcId: poi.srcPoiId,
			poiAccommodationSubType: poi.subType,
			poiStar: poi.stars ?? undefined,
			poiPrice: poi.startingPrice
				? formatPrice({
						amount: poi.startingPrice.amount,
						currency: poi.startingPrice.currency,
						language: languageTag(),
					})
				: undefined,
		};
	},
};

export function createBookingcomUrlWithLabel() {
	let platform: BookingcomPlatform;
	let locale: Locale;
	let pagename: Pagename;

	if (browser && page.data) {
		platform = getBookingPlatform();
		locale = page.data.locale;
		pagename = page.data.pagename;
	} else {
		// throw new Error('createBookingcomUrlWithLabel must be called in browser and page.data must be defined');
		platform = 'desktop';
		locale = 'en-US';
		pagename = 'homepage';
	}

	return {
		boxmap_itisearch_generic() {
			const bookingcomUrl = getBookingcomHomeUrl({ platform });
			return bookingComUrlSearchParam.setLabel(bookingcomUrl, locale, pagename, {
				contentType: 'boxmap',
			});
		},
		boxmap_itisearch_with_arrival(params: {
			arrival: BookingcomLabelBuildData['arrival'];
			arrivalCoordinates?: BookingcomUrlBuildParams['arrivalCoordinates'];
		}) {
			const { arrival, arrivalCoordinates } = params;
			const bookingcomUrl = getBookingcomSearchResultsUrl({ platform, arrival, arrivalCoordinates });

			return bookingComUrlSearchParam.setLabel(bookingcomUrl, locale, pagename, {
				contentType: 'boxmap',
				arrival,
			});
		},
		boxmap_mapresult_with_arrival(params: {
			arrival: NonNullable<BookingcomLabelBuildData['arrival']>;
			arrivalCoordinates: BookingcomUrlBuildParams['arrivalCoordinates'];
		}) {
			const { arrival, arrivalCoordinates } = params;
			const bookingcomUrl = getBookingcomSearchResultsUrl({ platform, arrival, arrivalCoordinates });

			return bookingComUrlSearchParam.setLabel(bookingcomUrl, locale, pagename, {
				contentType: 'boxmap',
				arrival,
				doAvailabilityCheck: getDoAvailabilityCheck(bookingcomUrl),
			});
		},
		poi_in_poilist(params: {
			poi: AccommodationPoiData;
			searchParams: SearchParams;
			arrival?: BookingcomLabelBuildData['arrival'];
		}) {
			const { poi, arrival, searchParams } = params;
			const bookingcomUrl = bookingComUrlSearchParam.setCheckinCheckout(poi.bookUrl, searchParams);

			return bookingComUrlSearchParam.setLabel(bookingcomUrl, locale, pagename, {
				contentType: 'poilist',
				...labelParams.fromPoi(poi),
				arrival,
				doAvailabilityCheck: getDoAvailabilityCheck(bookingcomUrl),
			});
		},
		poi_in_datasheet(params: {
			poi: AccommodationPoiData;
			searchParams: SearchParams;
			arrival?: BookingcomLabelBuildData['arrival'];
		}) {
			const { poi, arrival, searchParams } = params;
			const bookingcomUrl = bookingComUrlSearchParam.setCheckinCheckout(poi.bookUrl, searchParams);

			return bookingComUrlSearchParam.setLabel(bookingcomUrl, locale, pagename, {
				contentType: 'poilist',
				...labelParams.fromPoi(poi),
				arrival,
				doAvailabilityCheck: getDoAvailabilityCheck(bookingcomUrl),
			});
		},
		poi_datasheet_availability(params: {
			poi: AccommodationPoiData;
			arrival: BookingcomLabelBuildDataNullable['arrival'];
			departure: BookingcomLabelBuildDataNullable['departure'];
			searchParams: SearchParams;
		}) {
			const { poi, arrival, departure, searchParams } = params;
			const bookingcomUrl = bookingComUrlSearchParam.setCheckinCheckout(poi.bookUrl, searchParams);

			return bookingComUrlSearchParam.setLabel(bookingcomUrl, locale, pagename, {
				contentType: 'datasheet',
				...labelParams.fromPoi(poi),
				arrival,
				departure,
				doAvailabilityCheck: getDoAvailabilityCheck(bookingcomUrl),
			});
		},
		poilist_fallback(params: {
			arrival: NonNullable<BookingcomLabelBuildData['arrival']>;
			arrivalCoordinates: BookingcomUrlBuildParams['arrivalCoordinates'];
			searchParams: SearchParams;
		}) {
			const { arrival, arrivalCoordinates, searchParams } = params;
			let bookingcomUrl = getBookingcomSearchResultsUrl({ platform, arrival, arrivalCoordinates });
			bookingcomUrl = bookingComUrlSearchParam.setCheckinCheckout(bookingcomUrl, searchParams);

			return bookingComUrlSearchParam.setLabel(bookingcomUrl, locale, pagename, {
				contentType: 'poilist',
				arrival,
				doAvailabilityCheck: getDoAvailabilityCheck(bookingcomUrl),
			});
		},
		arrival_itiresult(params: {
			arrival: BookingcomLabelBuildData['arrival'];
			arrivalCoordinates?: BookingcomUrlBuildParams['arrivalCoordinates'];
			searchParams: SearchParams;
			departure: BookingcomLabelBuildData['departure'];
			distance: z.infer<typeof field.distance>;
		}) {
			const { arrival, arrivalCoordinates, searchParams, departure, distance } = params;
			let bookingcomUrl = getBookingcomSearchResultsUrl({ platform, arrival, arrivalCoordinates });
			bookingcomUrl = bookingComUrlSearchParam.setCheckinCheckout(bookingcomUrl, searchParams);

			return bookingComUrlSearchParam.setLabel(bookingcomUrl, locale, pagename, {
				contentType: 'arrival_zoi',
				arrival,
				departure,
				distance,
				doAvailabilityCheck: getDoAvailabilityCheck(bookingcomUrl),
			});
		},
		boxmap_itiresult(params: {
			arrival: BookingcomLabelBuildData['arrival'];
			arrivalCoordinates?: BookingcomUrlBuildParams['arrivalCoordinates'];
			departure: BookingcomLabelBuildData['departure'];
			distance?: z.infer<typeof field.distance>;
		}) {
			const { arrival, arrivalCoordinates, departure, distance } = params;
			const bookingcomUrl = getBookingcomSearchResultsUrl({ platform, arrival, arrivalCoordinates });

			return bookingComUrlSearchParam.setLabel(bookingcomUrl, locale, pagename, {
				contentType: 'boxmap',
				arrival,
				departure,
				distance,
				doAvailabilityCheck: getDoAvailabilityCheck(bookingcomUrl),
			});
		},
		// TODO: complete this
		search_extension(params: {
			seTouchpoint: NonNullable<BookingcomLabelBuildData['seTouchpoint']>;
			searchParams: SearchParams;
			arrival: NonNullable<BookingcomLabelBuildData['arrival']>;
			arrivalCoordinates: BookingcomUrlBuildParams['arrivalCoordinates'];
		}) {
			const { seTouchpoint, searchParams, arrival, arrivalCoordinates } = params;
			let bookingcomUrl = getBookingcomSearchResultsUrl({ platform, arrival, arrivalCoordinates });
			bookingcomUrl = bookingComUrlSearchParam.setCheckinCheckout(bookingcomUrl, searchParams);

			return bookingComUrlSearchParam.setLabel(bookingcomUrl, locale, pagename, {
				contentType: 'search_extension',
				seTouchpoint,
				doAvailabilityCheck: getDoAvailabilityCheck(bookingcomUrl),
			});
		},
	};
}

function getDoAvailabilityCheck(bookingcomUrl: string): z.infer<typeof field.doAvailabilityCheck> {
	return bookingComUrlSearchParam.hasCheckinCheckout(bookingcomUrl) ? 'On' : 'Off';
}

export function removeTimestampFromLabelOnBookingcomUrl(bookingcomUrl: string): string {
	const url = new URL(bookingcomUrl);
	const label = url.searchParams.get('label');

	if (!label) {
		return bookingcomUrl;
	}

	const labelParts = label.split('|');
	const timestampPosition = BOOKING_LABEL_SCHEMA.indexOf('timestamp');

	// Ensure the label has enough parts and timestamp exists in schema
	if (labelParts.length <= timestampPosition || timestampPosition === -1) {
		return bookingcomUrl;
	}

	// Clear the timestamp while preserving label structure
	labelParts[timestampPosition] = '';
	url.searchParams.set('label', labelParts.join('|'));

	return url.toString();
}
